<template>
  <div class="home">
    <section class="hero">
      <h1 class="title">Transfert soviétique</h1>
      <p class="subtitle">
        Partage de fichiers simple, rapide, et sans chichis. <br/>
        Glisse, envoie, partage — puis gère tes transferts en toute clarté.
      </p>

      <div class="storage-banner" v-if="storage.totalBytes">
        <div class="storage-row">
          <strong>Espace serveur utilisé :</strong>
          <span>{{ formatSize(storage.usedBytes) }} / {{ formatSize(storage.totalBytes) }}</span>
        </div>
        <div
            class="storage-bar"
            role="progressbar"
            :aria-valuemin="0"
            :aria-valuemax="100"
            :aria-valuenow="Math.round(usedPct)"
        >
          <div class="storage-bar-fill" :style="{ width: usedPct + '%' }"></div>
        </div>
        <p class="storage-note">
          C’est gratuit, mais l’espace est limité — merci de ne pas abuser 😉
        </p>
      </div>
      <div class="storage-banner" v-else>
        <div class="storage-row">
          <strong>Espace serveur :</strong>
          <span>chargement…</span>
        </div>
        <div class="storage-bar">
          <div class="storage-bar-fill" style="width: 0;"></div>
        </div>
      </div>

      <div class="cta-row">
        <router-link class="btn btn-primary" :to="{ name: 'send' }">
          🚀 Envoyer des fichiers
        </router-link>
        <router-link class="btn" :to="{ name: 'manage' }">
          🛠️ Gérer mes partages
        </router-link>
      </div>

      <div class="quick-open">
        <input
            class="open-input"
            v-model.trim="shareInput"
            type="text"
            placeholder="Coller un lien de partage ou un ID…"
            @keyup.enter="openShare"
        />
        <button class="btn" :disabled="!shareInput" @click="openShare">Ouvrir</button>
      </div>
    </section>

    <section class="features">
      <div class="card feature">
        <div class="icon" aria-hidden="true">📂</div>
        <h3>Ajout facile</h3>
        <p>Dépose tes fichiers ou clique pour les sélectionner. Taille totale affichée en temps réel.</p>
      </div>

      <div class="card feature">
        <div class="icon" aria-hidden="true">⏱️</div>
        <h3>Rétention au choix</h3>
        <p>Du <em>premier téléchargement</em> à <em>1 mois</em>. À toi de décider.</p>
      </div>

      <div class="card feature">
        <div class="icon" aria-hidden="true">📊</div>
        <h3>Suivi transparent</h3>
        <p>Vitesse, ETA, liens directs et gestion centralisée — tout est sous contrôle.</p>
      </div>

      <div class="card feature">
        <div class="icon" aria-hidden="true">🕵️</div>
        <h3>Anonyme ou lié</h3>
        <p>Envoi anonyme ou lié à un e-mail pour retrouver tes partages en un clic.</p>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = process.env.VUE_APP_API_URL || 'http://localhost:3000'

export default {
  name: 'HomeView',

  data() {
    return {
      shareInput: '',
      storage: {
        totalBytes: 0,
        usedBytes: 0,
        freeBytes: 0,
        loading: false,
        error: null
      },
      storageTimer: null
    }
  },

  computed: {
    usedPct() {
      const {totalBytes, usedBytes} = this.storage
      if (!totalBytes) return 0
      return Math.min(100, (usedBytes / totalBytes) * 100)
    }
  },

  mounted() {
    this.fetchStorage()
    this.storageTimer = setInterval(this.fetchStorage, 60000)
  },

  beforeUnmount() {
    if (this.storageTimer) clearInterval(this.storageTimer)
  },

  methods: {
    formatSize(b) {
      if (b < 1024) return b + ' B'
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB'
      if (b < 1024 * 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB'
      return (b / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
    },

    async fetchStorage() {
      this.storage.loading = true
      this.storage.error = null
      try {
        const {data} = await axios.get(`${API_BASE}/storage`, {timeout: 3000})
        this.storage.totalBytes = Number(data?.totalBytes) || 0
        this.storage.usedBytes = Number(data?.usedBytes) || 0
        this.storage.freeBytes = Number(data?.freeBytes) || 0
      } catch (e) {
        this.storage.error = 'Impossible de récupérer l’espace disque'
      } finally {
        this.storage.loading = false
      }
    },

    extractId(raw) {
      if (!raw) return ''
      const v = raw.trim()
      try {
        const url = new URL(v)
        const byQuery = url.searchParams.get('id')
        if (byQuery) return byQuery
        const parts = url.pathname.split('/').filter(Boolean)
        if (parts.length) return parts[parts.length - 1]
      } catch {
        console.log("Couldn't parse URL: ", v, " - falling back to query param");
      }
      const m = v.match(/(?:^|[?&#])id=([^&#]+)/i)
      if (m) return decodeURIComponent(m[1])
      return v
    },

    async openShare() {
      const id = this.extractId(this.shareInput)
      if (!id) return
      try {
        await this.$router.push({name: 'share', query: {id}})
      } catch {
        try {
          await this.$router.push({path: '/share', query: {id}})
        } catch {
          window.location.hash = `#/share?id=${encodeURIComponent(id)}`
        }
      }
    }
  }
}
</script>

<style scoped>
.home {
  display: grid;
  gap: 2rem;
  padding: 2rem;
}

.hero {
  width: min(1000px, 96%);
  margin: 0 auto;
  background: var(--gold-08);
  border: 3px dashed var(--gold);
  border-radius: 14px;
  padding: 1.4rem 1.6rem;
  text-align: center;
}

.title {
  font-family: 'Impact', sans-serif;
  font-size: clamp(2rem, 3.6vw, 3rem);
  color: var(--soviet-red-dark);
  margin: 0 0 .4rem;
  letter-spacing: .5px;
}

.subtitle {
  margin: 0 auto 1rem;
  color: #5a3f00;
  max-width: 58ch;
  line-height: 1.4;
}

.storage-banner {
  width: min(780px, 96%);
  margin: .8rem auto 0;
  text-align: left;
  display: grid;
  gap: .35rem;
}

.storage-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  color: var(--soviet-red-dark);
}

.storage-bar {
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid var(--gold-16);
  overflow: hidden;
}

.storage-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold) 0%, var(--gold-light) 100%);
  transition: width .25s ease;
}

.storage-note {
  margin: 0;
  font-size: .9rem;
  color: #7a5a0a;
  opacity: .9;
}

.cta-row {
  display: flex;
  gap: .6rem;
  justify-content: center;
  flex-wrap: wrap;
  margin: 1rem 0 .6rem;
}

.btn {
  border: 3px dashed var(--gold);
  background: #fff;
  color: var(--soviet-red-dark);
  border-radius: 12px;
  padding: .7rem 1.1rem;
  cursor: pointer;
  text-decoration: none;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  display: inline-flex;
  align-items: center;
  gap: .4rem;
}

.btn:hover {
  background: var(--gold-16);
  border-color: var(--gold-light);
}

.btn:active {
  background: var(--gold-28);
}

.btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--gold-08);
}

.quick-open {
  display: flex;
  gap: .5rem;
  justify-content: center;
  margin-top: .6rem;
  flex-wrap: wrap;
}

.open-input {
  width: min(520px, 92%);
  padding: .65rem .8rem;
  border-radius: 10px;
  border: 1px solid var(--gold-16);
  background: #fff;
}

.hint {
  margin: .6rem 0 0;
  font-size: .95rem;
  color: #7a5a0a;
}

.features {
  width: min(1100px, 96%);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: .9rem;
}

.card.feature {
  background: #fff;
  border: 3px dashed var(--gold);
  border-radius: 14px;
  padding: 1rem;
  display: grid;
  gap: .35rem;
  text-align: center;
  transition: transform .08s ease, background-color .15s ease;
}

.card.feature:hover {
  background: var(--gold-08);
  transform: translateY(-2px);
}

.icon {
  font-size: 1.8rem;
}

.feature h3 {
  margin: .2rem 0 0;
  color: var(--soviet-red-dark);
}

.feature p {
  margin: 0;
  color: #5a3f00;
}
</style>
